import React, { useState, useEffect, useCallback } from 'react';
import { fetchUserByMobile, createComplaint } from '../actions/action';
import {
  DELIVERY_COMPLAINT_CATEGORIES,
  PRODUCT_COMPLAINT_CATEGORIES,
  SERVICE_REQUEST_TYPES,
  SERVICE_REQUEST_WATER_PURIFIER,
} from '../../../../constants/complaintTypes';
import { useDropzone } from 'react-dropzone';
import { Offcanvas, OffcanvasBody, OffcanvasHeader, Spinner } from 'reactstrap';
import toast, { Toaster } from 'react-hot-toast';

const CreateComplaintOffcanvas = ({
  isOpen,
  toggle,
  users,
  onComplaintCreated,
}) => {
  const [mobile, setMobile] = useState('');
  const [searched, setSearched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isDataLoading, setDataLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [expandedOrderNo, setExpandedOrderNo] = useState(null);
  const [selectedSkus, setSelectedSkus] = useState([]);

  const toggleSku = (sku) => {
    setSelectedSkus((prev) =>
      prev.includes(sku) ? prev.filter((s) => s !== sku) : [...prev, sku]
    );
  };
  const [form, setForm] = useState({
    fullname: '',
    mobile: '',
    email: '',
    serviceRequest: '',
    type: '',
    maintenanceType: '',
    qty: '',
    orderNo: '',
    sku: '',
    remarks: '',
    files: [],
    accept: false,
  });

  const handleSearch = async (e) => {
    e.preventDefault();
    setLoading(true);
    setOrders([]);
    setSelectedOrder(null);
    setForm({ fullname: '', mobile: '', email: '', serviceRequest: '', type: '', maintenanceType: '', qty: '', orderNo: '', sku: '', remarks: '', files: [], accept: false });
    try {
      const data = await fetchUserByMobile({ mobile });
      const orderList = Array.isArray(data?.data) ? data.data : [];
      setOrders(orderList);
    } catch (error) {
      setOrders([]);
    }
    setSearched(true);
    setLoading(false);
  };

  const handleSelectOrder = (order) => {
    setSelectedOrder(order);
    setForm((prev) => ({
      ...prev,
      fullname: order.customer_name || '',
      mobile,
      orderNo: order.order_no || '',
      sku: selectedSkus.join(', '),
    }));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
      // A Sub Type only belongs to the Type it was picked under
      ...(name === 'type' ? { maintenanceType: '' } : {}),
    }));
  };

  // File upload constraints
  const MAX_FILES = 5;
  const MAX_TOTAL_SIZE = 5 * 1024 * 1024; // 5MB
  const [fileError, setFileError] = useState('');

  // Drag and drop handler for files
  const onDrop = useCallback(
    (acceptedFiles) => {
      setFileError('');
      setForm((prev) => {
        const newFiles = [...prev.files, ...acceptedFiles];
        if (newFiles.length > MAX_FILES) {
          setFileError('You can upload a maximum of 5 images.');
          return prev;
        }
        const totalSize = newFiles.reduce((sum, file) => sum + file.size, 0);
        if (totalSize > MAX_TOTAL_SIZE) {
          setFileError('Total file size must be less than 5MB.');
          return prev;
        }
        return {
          ...prev,
          files: newFiles,
        };
      });
    },
    [MAX_FILES, MAX_TOTAL_SIZE]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: true,
    accept: 'image/jpeg, image/png, image/jpg',
    maxFiles: MAX_FILES,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Map form fields to API fields
    const payload = {
      customer_name: form.fullname,
      country: 'ae', // You may want to make this dynamic
      mobile: form.mobile,
      order_no: selectedOrder?.order_no || '',
      sku: selectedSkus.join(','),
      complaint_remark: form.remarks,
      complaint_category: form.serviceRequest,
      complaint_subcategory: form.type,
      complaint_detail_category: form.maintenanceType,
      created_by: users?.user_name || '', // Set this from user context if available
      oms_user_id: users?.user_id || '', // Set this from user context if available
      email: form.email,
      files: form.files,
    };
    try {
      setDataLoading(true);
      await createComplaint(payload);
      toast.success('Request submitted!', {
        style: {
          border: '1px solid #fff',
          padding: '20px',
          color: '#ffffff',
          background: '#4CAF50',
        },
        iconTheme: {
          primary: '#FFFAEE',
          secondary: '#4CAF50',
        },
      });
      if (onComplaintCreated) onComplaintCreated();
      toggle();
    } catch (err) {
      toast.error('Failed to submit request.', {
        style: {
          border: '1px solid #fff',
          padding: '20px',
          color: '#ffffff',
          background: '#f44336',
        },
        iconTheme: {
          primary: '#FFFAEE',
          secondary: '#f44336',
        },
      });
    } finally {
      setDataLoading(false);
    }
  };

  // Reset form and search state when offcanvas is closed
  useEffect(() => {
    if (!isOpen) {
      setMobile('');
      setSearched(false);
      setOrders([]);
      setSelectedOrder(null);
      setExpandedOrderNo(null);
      setSelectedSkus([]);
      setForm({
        fullname: '',
        mobile: '',
        email: '',
        serviceRequest: '',
        type: '',
        maintenanceType: '',
        qty: '',
        orderNo: '',
        sku: '',
        remarks: '',
        files: [],
        accept: false,
      });
    }
  }, [isOpen]);

  // Simple shimmer component
  const Shimmer = () => (
    <div style={{ padding: 16 }}>
      <div
        className="shimmer shimmer-line"
        style={{ height: 32, width: '40%', marginBottom: 16 }}
      />
      <div
        className="shimmer shimmer-line"
        style={{ height: 32, width: '60%', marginBottom: 16 }}
      />
      <div
        className="shimmer shimmer-line"
        style={{ height: 32, width: '80%', marginBottom: 16 }}
      />
      <div
        className="shimmer shimmer-line"
        style={{ height: 32, width: '50%', marginBottom: 16 }}
      />
    </div>
  );

  // Add shimmer CSS
  const shimmerStyle = `
.shimmer {
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 37%, #f0f0f0 63%);
  background-size: 400% 100%;
  animation: shimmer 1.2s ease-in-out infinite;
  border-radius: 6px;
}
@keyframes shimmer {
  0% { background-position: -400px 0; }
  100% { background-position: 400px 0; }
}
`;
  if (
    typeof document !== 'undefined' &&
    !document.getElementById('shimmer-style')
  ) {
    const style = document.createElement('style');
    style.id = 'shimmer-style';
    style.innerHTML = shimmerStyle;
    document.head.appendChild(style);
  }

  return (
    <Offcanvas
      isOpen={isOpen}
      toggle={toggle}
      direction="end"
      style={{ width: '70vw', maxWidth: '80vw' }}
    >
      <OffcanvasHeader toggle={toggle}>Create Request</OffcanvasHeader>
      <OffcanvasBody>
        <form onSubmit={handleSearch} style={{ marginBottom: 16 }}>
          <div className="row d-flex align-items-end">
            <div className="mb-3 col-10">
              <label htmlFor="mobileSearch" className="form-label">
                Mobile Number
              </label>
              <input
                type="text"
                className="form-control"
                id="mobileSearch"
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
                placeholder="Enter mobile number"
                required
              />
            </div>
            <div className="mb-3 col-2">
              <button type="submit" className="btn btn-primary w-100">
                Search
              </button>
            </div>
          </div>
        </form>
        {loading && <Shimmer />}
        {searched && !loading && orders.length === 0 && (
          <div className="alert alert-warning py-2">No orders found for this phone number.</div>
        )}
        {searched && !loading && orders.length > 0 && !selectedOrder && (
          <div className="mb-3">
            <p className="fw-semibold mb-2 text-muted small">{orders.length} order{orders.length > 1 ? 's' : ''} found — select one to continue</p>
            <div style={{ maxHeight: 460, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
              {orders.map((order) => {
                const isExpanded = expandedOrderNo === order.order_no;
                return (
                  <div key={order.order_no} className="border rounded-3" style={{ background: '#fff' }}>
                    {/* Order header row — always visible */}
                    <div
                      className="d-flex align-items-center gap-3 px-3 py-2"
                      style={{ cursor: 'pointer', borderBottom: isExpanded ? '1px solid #e9ecef' : 'none', background: isExpanded ? '#f0f4ff' : '#fff' }}
                      onClick={() => { setExpandedOrderNo(isExpanded ? null : order.order_no); setSelectedSkus([]); }}
                    >
                      <span className="fw-semibold" style={{ fontSize: 13, minWidth: 120 }}>{order.order_no}</span>
                      <span className="badge bg-soft-info text-dark" style={{ fontSize: 11 }}>{order.status}</span>
                      <span className="text-muted" style={{ fontSize: 12 }}>
                        <i className="bx bx-calendar me-1"></i>{order.order_date?.split(' ')[0]}
                      </span>
                      <span className="ms-auto d-flex align-items-center gap-2 text-muted" style={{ fontSize: 12 }}>
                        <i className="bx bx-package"></i>{order.items?.length || 0} item{order.items?.length !== 1 ? 's' : ''}
                        <i className={`bx ${isExpanded ? 'bx-chevron-up' : 'bx-chevron-down'}`} style={{ fontSize: 16 }}></i>
                      </span>
                    </div>

                    {/* Expanded items */}
                    {isExpanded && (
                      <div style={{ borderTop: '1px solid #e9ecef', background: '#f8f9fa', borderRadius: '0 0 12px 12px', padding: '12px' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
                          {order.items?.map((item) => {
                            const isChecked = selectedSkus.includes(item.sku);
                            return (
                              <div
                                key={item.sku}
                                onClick={() => toggleSku(item.sku)}
                                style={{ display: 'flex', alignItems: 'center', gap: 12, background: isChecked ? '#f0f4ff' : '#fff', border: `1.5px solid ${isChecked ? '#4a6cf7' : '#e9ecef'}`, borderRadius: 10, padding: '10px 12px', cursor: 'pointer', transition: 'all 0.15s' }}
                              >
                                <input
                                  type="checkbox"
                                  checked={isChecked}
                                  onChange={() => toggleSku(item.sku)}
                                  onClick={(e) => e.stopPropagation()}
                                  style={{ width: 16, height: 16, flexShrink: 0, accentColor: '#000', cursor: 'pointer' }}
                                />
                                {item.image ? (
                                  <img src={item.image} alt={item.name} width={52} height={52}
                                    style={{ objectFit: 'cover', borderRadius: 8, flexShrink: 0, border: '1px solid #eee' }} />
                                ) : (
                                  <div style={{ width: 52, height: 52, flexShrink: 0, background: '#f0f0f0', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <i className="bx bx-image text-muted" style={{ fontSize: 22 }}></i>
                                  </div>
                                )}
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ fontSize: 13, fontWeight: 600, lineHeight: 1.4, marginBottom: 4 }}>{item.name}</div>
                                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                    <span className="badge bg-dark" style={{ fontSize: 10 }}>{item.sku}</span>
                                    <span style={{ fontSize: 12, color: '#6c757d' }}>Qty: {item.qty}</span>
                                    <span style={{ fontSize: 12, color: '#6c757d' }}>AED {item.item_price}</span>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        <button
                          type="button"
                          className="btn btn-dark w-100"
                          style={{ padding: '12px', fontSize: 14 }}
                          onClick={() => handleSelectOrder(order)}
                          disabled={selectedSkus.length === 0}
                        >
                          {selectedSkus.length > 0 ? `Select Order with ${selectedSkus.length} item${selectedSkus.length > 1 ? 's' : ''}` : 'Select items to continue'}
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {searched && !loading && selectedOrder && (
          <form onSubmit={handleSubmit}>
            <div className="d-flex align-items-center gap-2 mb-3 p-2 rounded-3 border bg-soft-light">
              <span className="fw-semibold small">Order: {selectedOrder.order_no}</span>
              <span className="text-muted small">&bull; {selectedOrder.customer_name}</span>
              <button type="button" className="btn btn-sm btn-outline-secondary ms-auto" onClick={() => setSelectedOrder(null)}>
                <i className="bx bx-arrow-back me-1"></i>Change Order
              </button>
            </div>
            <div className="row">
              <div className="mb-3 col-4">
                <label className="form-label">Fullname</label>
                <input
                  type="text"
                  className="form-control"
                  name="fullname"
                  value={form.fullname}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="mb-3 col-4">
                <label className="form-label">Mobile Number</label>
                <input
                  type="text"
                  className="form-control"
                  name="mobile"
                  value={form.mobile}
                  onChange={handleChange}
                  required
                  disabled
                />
              </div>
              <div className="mb-3 col-4">
                <label className="form-label">Email ID</label>
                <input
                  type="email"
                  className="form-control"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="mb-3 col-12">
                <label className="form-label">
                  Service Request Information
                </label>
                <div className="d-flex gap-2 flex-wrap">
                  {[
                    // {
                    //   value: 'Installation Service',
                    //   icon: (
                    //     <i className="bx bx-layer" style={{ fontSize: 20 }}></i>
                    //   ),
                    // },
                    {
                      value: 'Product Complaints',
                      icon: (
                        <i className="bx bx-box" style={{ fontSize: 20 }}></i>
                      ),
                    },
                    {
                      value: 'Delivery Complaints',
                      icon: (
                        <i
                          className="bx bxs-truck"
                          style={{ fontSize: 20 }}
                        ></i>
                      ),
                    },
                  ].map((option) => (
                    <label
                      key={option.value}
                      className={`btn btn-outline-secondary d-flex align-items-center mb-0${
                        form.serviceRequest === option.value
                          ? ' selected-service-request'
                          : ''
                      }`}
                      style={{
                        background:
                          form.serviceRequest === option.value ? '#000' : '',
                        color:
                          form.serviceRequest === option.value ? '#fff' : '',
                        minWidth: 220,
                        cursor: 'pointer',
                        padding: '1rem',
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: 8,
                        borderRadius: 10,
                      }}
                    >
                      <span
                        style={{
                          position: 'relative',
                          display: 'inline-block',
                          marginRight: 8,
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={form.serviceRequest === option.value}
                          onChange={() =>
                            setForm((f) => ({
                              ...f,
                              serviceRequest: option.value,
                              type: '',
                              maintenanceType: '',
                              qty: '',
                              remarks: '',
                              files: [],
                              accept: false,
                              orderNo: '',
                              sku: '',
                            }))
                          }
                          className="form-check-input"
                          style={{
                            marginLeft: 0,
                            marginRight: 0,
                            verticalAlign: 'middle',
                            color: '#000',
                            borderRadius: '50%',
                            width: 20,
                            height: 20,
                            border:
                              form.serviceRequest === option.value
                                ? '2px solid #fff'
                                : '2px solid #000',
                            appearance: 'none',
                            outline: 'none',
                            background:
                              form.serviceRequest === option.value
                                ? '#000'
                                : '#fff',
                            cursor: 'pointer',
                            display: 'inline-block',
                            position: 'relative',
                          }}
                        />
                        {form.serviceRequest === option.value && (
                          <span
                            style={{
                              position: 'absolute',
                              left: 0,
                              top: 0,
                              width: 21,
                              height: 26,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: '#fff',
                              fontSize: 16,
                              pointerEvents: 'none',
                              userSelect: 'none',
                            }}
                          >
                            ✓
                          </span>
                        )}
                      </span>
                      <span
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          marginRight: 8,
                        }}
                      >
                        {option.icon}
                      </span>
                      <span style={{ display: 'flex', alignItems: 'center' }}>
                        {option.value}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
              <div
                className={`mb-3 col-4 ${form.serviceRequest ? '' : 'd-none'}`}
              >
                <label className="form-label">
                  {form.serviceRequest === 'Installation Service'
                    ? 'Request Type'
                    : 'Type'}
                </label>
                <select
                  className="form-select"
                  name="type"
                  value={form.type}
                  onChange={handleChange}
                  required
                >
                  <option value="" disabled selected>
                    Select Type
                  </option>
                  {form.serviceRequest === 'Installation Service' ? (
                    <>
                      {SERVICE_REQUEST_TYPES.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </>
                  ) : form.serviceRequest === 'Product Complaints' ? (
                    <>
                      {PRODUCT_COMPLAINT_CATEGORIES.map((category) => (
                        <option key={category.value} value={category.value}>
                          {category.label}
                        </option>
                      ))}
                    </>
                  ) : form.serviceRequest === 'Delivery Complaints' ? (
                    <>
                      {DELIVERY_COMPLAINT_CATEGORIES.map((category) => (
                        <option key={category.value} value={category.value}>
                          {category.label}
                        </option>
                      ))}
                    </>
                  ) : null}
                </select>
              </div>
              <div
                className={`mb-3 col-4 ${
                  form.serviceRequest === 'Installation Service' && form.type
                    ? ''
                    : 'd-none'
                }`}
              >
                <label className="form-label">Sub Type</label>
                <select
                  className="form-select"
                  name="maintenanceType"
                  value={form.maintenanceType}
                  onChange={handleChange}
                >
                  <option value="" disabled selected>
                    Select Sub Type
                  </option>
                  {form.type === 'Water Purifier' ? (
                    <>
                      {SERVICE_REQUEST_WATER_PURIFIER.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </>
                  ) : null}
                </select>
              </div>
              <div
                className={`mb-3 col-4 ${
                  form.serviceRequest &&
                  form.type === 'Water Purifier' &&
                  form.maintenanceType
                    ? ''
                    : 'd-none'
                }`}
              >
                <label className="form-label">Number of Qty</label>
                <input
                  type="number"
                  className="form-control"
                  name="qty"
                  value={form.qty}
                  onChange={handleChange}
                  min="1"
                />
              </div>
              <div className="mb-3 col-12">
                <label className="form-label">Remarks</label>
                <textarea
                  className="form-control"
                  name="remarks"
                  value={form.remarks}
                  onChange={handleChange}
                  rows={3}
                />
              </div>
              <div className="mb-3 col-12">
                <label className="form-label">Upload Files</label>
                <div
                  {...getRootProps()}
                  style={{
                    border: '2px dashed #aaa',
                    borderRadius: 8,
                    padding: '1.5rem',
                    textAlign: 'center',
                    background: isDragActive ? '#f0f8ff' : '#fafbfc',
                    cursor: 'pointer',
                    marginBottom: 8,
                  }}
                >
                  <input
                    {...getInputProps()}
                    accept="image/jpeg, image/png, image/jpg"
                  />
                  {isDragActive ? (
                    <p>Drop the files here ...</p>
                  ) : (
                    <p>
                      <i
                        className="bx bx-cloud-upload"
                        style={{ fontSize: 35 }}
                      ></i>
                      <br />
                      Drag & drop files here, or click to select files
                    </p>
                  )}
                </div>
                {fileError && (
                  <div className="text-danger mt-1" style={{ fontSize: 13 }}>
                    {fileError}
                  </div>
                )}
                {form.files && form.files.length > 0 && (
                  <ul className="list-group mt-2">
                    {Array.from(form.files).map((file, idx) => (
                      <li
                        className="list-group-item py-1 px-2 d-flex align-items-center justify-content-between"
                        key={idx}
                        style={{ fontSize: 14 }}
                      >
                        <span>
                          <i
                            className="bx bx-paperclip"
                            style={{ marginRight: 6 }}
                          ></i>
                          {file.name}{' '}
                          <span style={{ color: '#888', fontSize: 12 }}>
                            ({(file.size / 1024).toFixed(1)} KB)
                          </span>
                        </span>
                        <button
                          type="button"
                          className="btn btn-sm btn-link text-danger ms-2"
                          title="Remove file"
                          style={{ textDecoration: 'none', fontSize: 18 }}
                          onClick={() => {
                            setForm((prev) => {
                              const newFiles = prev.files.filter(
                                (_, i) => i !== idx
                              );
                              return { ...prev, files: newFiles };
                            });
                            setFileError('');
                          }}
                        >
                          <i className="bx bx-x"></i>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="mb-3 col-12">
                <input
                  className="form-check-input me-2"
                  type="checkbox"
                  name="accept"
                  id="accept"
                  checked={form.accept}
                  onChange={handleChange}
                  required
                />
                <label className="form-check-label" htmlFor="accept">
                  I have read and accept the Terms of Service
                </label>
              </div>
              <div className="mb-0 col-2">
                <button
                  type="submit"
                  className="btn w-100 d-flex align-items-center justify-content-center gap-2"
                  style={{ backgroundColor: '#000', color: '#fff' }}
                >
                  {isDataLoading ? (
                    <span className="d-flex align-items-center gap-2">
                      <Spinner color="white" size="sm" />
                    </span>
                  ) : (
                    <span>Submit</span>
                  )}
                </button>
              </div>
            </div>
          </form>
        )}
        <Toaster position="top-right" reverseOrder={false} />
      </OffcanvasBody>
    </Offcanvas>
  );
};

export default CreateComplaintOffcanvas;
